import { ThemeEvents } from '@theme/events';

/**
 * B&S Parfums design wiring (Zone A).
 * Replaces the design prototype script: every behavior it faked is wired here
 * to the host theme's real mechanisms (cart drawer, CartUpdateEvent,
 * VariantUpdateEvent) or implemented standalone (mobile menu, accordion,
 * gallery thumbs, sticky ATC reveal).
 */

const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function on(selector, handler) {
  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const target = event.target.closest(selector);
    if (target) handler(target, event);
  });
}

/* --- Cart: open host drawer from the design's pill button --- */
on('[data-cart-toggle]', (_, event) => {
  const drawer = document.querySelector('cart-drawer-component');
  if (drawer && typeof drawer.open === 'function') {
    event.preventDefault();
    drawer.open();
  }
});

/* --- Cart: live count + total in the design header --- */
/* Bubbles that only make sense with something in the cart (mobile header icon). */
function toggleCartBadges(itemCount) {
  document.querySelectorAll('[data-cart-count-badge]').forEach((el) => {
    el.hidden = itemCount === 0;
  });
}

async function refreshCartSummary(itemCount) {
  if (typeof itemCount === 'number') {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = String(itemCount);
    });
    toggleCartBadges(itemCount);
  }
  try {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const cart = await fetch(`${root}cart.js`).then((r) => r.json());
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = String(cart.item_count);
    });
    toggleCartBadges(cart.item_count);
    const formatted = new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: cart.currency || 'EUR',
    }).format(cart.total_price / 100);
    document.querySelectorAll('[data-cart-total]').forEach((el) => {
      el.textContent = formatted.replace(/^(\D+)\s?/, '€');
    });
  } catch {
    /* header badge falls back to server-rendered values */
  }
}
document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  refreshCartSummary(event.detail?.data?.itemCount);
});

/* --- Mobile menu (design's own markup) --- */
function closeMenu() {
  document.querySelector('.m-menu')?.classList.remove('open');
  document.querySelector('.m-menu__ov')?.classList.remove('open');
}
on('[data-menu-toggle]', () => {
  document.querySelector('.m-menu')?.classList.add('open');
  document.querySelector('.m-menu__ov')?.classList.add('open');
});
on('[data-menu-close]', closeMenu);
on('.m-menu__ov', closeMenu);

/* --- Accordion (design markup, max-height transition) --- */
on('.acc__head', (head) => {
  const item = head.closest('.acc__item');
  const body = item?.querySelector('.acc__body');
  if (!item || !body) return;
  if (item.classList.contains('open')) {
    item.classList.remove('open');
    body.style.maxHeight = '0px';
  } else {
    item.classList.add('open');
    body.style.maxHeight = `${body.scrollHeight}px`;
  }
});
document.querySelectorAll('.acc__item.open .acc__body').forEach((body) => {
  body.style.maxHeight = `${body.scrollHeight}px`;
});

/* --- PDP gallery: thumb select + per-variant filtering --- */

/**
 * The real main image — never the peek layer the swipe inserts alongside it.
 * @param {Element} gallery
 * @returns {HTMLImageElement | null}
 */
function bsMainImage(gallery) {
  return /** @type {HTMLImageElement | null} */ (
    gallery.querySelector('.pdp__main .frozen-media:not(.pdp__peek) img')
  );
}

/* data-full is the 2000px master, which the zoom view genuinely needs. The main
   image does not: ask the CDN for what the box actually shows at this DPR. On a
   390px phone that is ~1200px, where 2000px would be ~2.8x the pixels for no
   visible gain. Never BELOW box x DPR though — that was the "low res" bug. */
/**
 * @param {HTMLElement} thumb
 * @param {Element} gallery
 * @returns {string | null}
 */
function bsMediaSrc(thumb, gallery) {
  const full = thumb.dataset.full;
  if (!full) return null;
  const box = gallery.querySelector('.pdp__main')?.clientWidth;
  if (!box) return full;
  const needed = box * (window.devicePixelRatio || 1);
  // Rounded to 100s so repeat views hit the same CDN variant instead of minting
  // a new render per device width.
  const width = Math.min(2000, Math.max(400, Math.ceil(needed / 100) * 100));
  return full.replace(/([?&]width=)\d+/, `$1${width}`);
}

function bsSelectMedia(gallery, thumb) {
  const mainImg = bsMainImage(gallery);
  if (!mainImg || !thumb) return;
  gallery.querySelectorAll('.pdp__thumb').forEach((t) => t.classList.remove('on'));
  thumb.classList.add('on');
  // Never copy the 400px thumbnail src into the main — that was the "low res" bug.
  const src = bsMediaSrc(thumb, gallery);
  if (src) {
    mainImg.removeAttribute('srcset');
    mainImg.removeAttribute('sizes');
    mainImg.src = src;
  }
  // Mirror into the sticky bar's thumbnail. Copy the THUMB's source, not data-full —
  // a 2000px file behind a 42px box would be a pointless download.
  const stickyImg = document.querySelector('[data-bs-sticky-img] img');
  const thumbImg = thumb.querySelector('img');
  if (stickyImg && thumbImg) {
    stickyImg.removeAttribute('srcset');
    stickyImg.removeAttribute('sizes');
    stickyImg.src = thumbImg.currentSrc || thumbImg.src;
  }
}
on('.pdp__thumb', (thumb) => {
  const gallery = thumb.closest('.pdp__gallery');
  if (gallery) bsSelectMedia(gallery, thumb);
});

/* Swipe left/right on the main image to step through the gallery (touch only).
   Steps through the same thumb strip the taps use, so the variant filter (hidden
   thumbs) and the sticky-bar mirror keep working — this is only a second way in.

   The image tracks the finger: the neighbour is rendered into a peek layer that
   slides in alongside, so a half-swipe shows what is coming and springs back if
   released short. Without that the image just snaps at the end and the gesture
   reads as a button, not a carousel. */
const SWIPE_MIN_DISTANCE = 40;
const SWIPE_AXIS_LOCK = 8;
const SWIPE_SLIDE_MS = 220;

/**
 * @param {Element} gallery
 * @returns {HTMLElement[]}
 */
function bsVisibleThumbs(gallery) {
  const thumbs = /** @type {HTMLElement[]} */ (Array.from(gallery.querySelectorAll('.pdp__thumb')));
  return thumbs.filter((t) => !t.hidden);
}

/**
 * The thumb `direction` steps away from the active one, wrapping at both ends.
 * @param {Element} gallery
 * @param {number} direction
 * @returns {HTMLElement | null}
 */
function bsNeighbourThumb(gallery, direction) {
  const thumbs = bsVisibleThumbs(gallery);
  if (thumbs.length < 2) return null;
  // The active thumb can be filtered out by a variant switch; start from the
  // first visible one then, so a backwards swipe doesn't land near the end.
  const current = thumbs.findIndex((t) => t.classList.contains('on'));
  const next = current === -1 ? 0 : (current + direction + thumbs.length) % thumbs.length;
  return thumbs[next] ?? null;
}

/**
 * @param {Element} gallery
 * @param {number} direction
 */
function bsStepMedia(gallery, direction) {
  const target = bsNeighbourThumb(gallery, direction);
  if (target) bsSelectMedia(gallery, target);
}

/**
 * Lazily built second media layer, appended AFTER the real one so that
 * bsMainImage() and any `.pdp__main img` lookup still find the original.
 * @param {HTMLElement} main
 * @returns {HTMLElement}
 */
function bsPeekLayer(main) {
  const existing = /** @type {HTMLElement | null} */ (main.querySelector('.pdp__peek'));
  if (existing) return existing;
  const peek = document.createElement('div');
  peek.className = 'frozen-media pdp__peek';
  peek.setAttribute('aria-hidden', 'true');
  peek.hidden = true;
  const img = document.createElement('img');
  img.className = 'frozen-img';
  img.alt = '';
  peek.appendChild(img);
  main.appendChild(peek);
  return peek;
}

/**
 * @typedef {object} BsSwipeState
 * @property {number} id - identifier of the finger that owns the gesture
 * @property {number} x
 * @property {number} y
 * @property {number} lastX - last tracked position, so a cancel can still settle
 * @property {HTMLElement} main - .pdp__main
 * @property {Element} gallery - .pdp__gallery
 * @property {HTMLElement} media - the real .frozen-media layer
 * @property {'x' | 'y' | null} axis - locked on the first decisive movement
 * @property {number} direction - -1 previous, 1 next, 0 undecided
 * @property {HTMLElement | null} peek
 * @property {HTMLElement | null} target - thumb the gesture would land on
 * @property {number} width
 */

/** @type {BsSwipeState | null} */
let bsSwipe = null;

/* A finished drag is followed by a synthetic click. Swallow it, so the zoom view
   we hang on the main image later opens on taps only, never on swipes. */
let bsSwipeEndedAt = -Infinity;
const SWIPE_CLICK_GUARD_MS = 500;

/**
 * Point the peek layer at the neighbour in `direction` and park it off-screen.
 * @param {BsSwipeState} state
 * @param {number} direction
 */
function bsPreparePeek(state, direction) {
  const target = bsNeighbourThumb(state.gallery, direction);
  if (!target) return false;
  const peek = bsPeekLayer(state.main);
  const img = /** @type {HTMLImageElement | null} */ (peek.querySelector('img'));
  const src = bsMediaSrc(target, state.gallery);
  // Same URL the commit will put on the main image, so that swap is a cache hit.
  if (img && src && img.getAttribute('src') !== src) img.src = src;
  peek.hidden = false;
  state.peek = peek;
  state.target = target;
  state.direction = direction;
  return true;
}

/**
 * @param {BsSwipeState} state
 * @param {number} deltaX
 */
function bsDrawSwipe(state, deltaX) {
  const { media, peek, direction, width } = state;
  media.style.transform = `translate3d(${deltaX}px, 0, 0)`;
  // Incoming image sits one full box away on the side it travels in from.
  if (peek) peek.style.transform = `translate3d(${deltaX + direction * width}px, 0, 0)`;
}

/** @param {BsSwipeState} state */
function bsResetSwipeLayers(state) {
  const { media, peek } = state;
  media.style.transition = '';
  media.style.transform = '';
  if (peek) {
    peek.style.transition = '';
    peek.style.transform = '';
    peek.hidden = true;
  }
  delete state.main.dataset.sliding;
}

/**
 * Settle the drag: `commit` slides the neighbour in, otherwise it springs back.
 * @param {BsSwipeState} state
 * @param {boolean} commit
 */
function bsSettleSwipe(state, commit) {
  const { media, peek, direction, width, gallery, target } = state;
  const transition = `transform ${SWIPE_SLIDE_MS}ms ease-out`;
  state.main.dataset.sliding = '1';
  media.style.transition = transition;
  media.style.transform = commit ? `translate3d(${-direction * width}px, 0, 0)` : 'translate3d(0, 0, 0)';
  if (peek) {
    peek.style.transition = transition;
    peek.style.transform = commit ? 'translate3d(0, 0, 0)' : `translate3d(${direction * width}px, 0, 0)`;
  }

  // Fixed timer rather than transitionend: the latter never fires when the drag
  // happens to end exactly on the target offset.
  setTimeout(async () => {
    if (!commit || !target) {
      bsResetSwipeLayers(state);
      return;
    }
    // The peek now covers the box exactly. Swap the main image underneath it and
    // only drop the cover once the new source has painted — otherwise the reset
    // flashes the previous image for a frame.
    bsSelectMedia(gallery, target);
    const mainImg = bsMainImage(gallery);
    try {
      // Raced against a timer: a fast swipe can commit before the peek's own
      // request finished, and a stalled image must never strand the gallery
      // behind the cover with `sliding` still set.
      await Promise.race([
        mainImg?.decode(),
        new Promise((resolve) => setTimeout(resolve, SWIPE_SLIDE_MS * 2)),
      ]);
    } catch {
      /* decode() rejects on a replaced src; the cover drops either way */
    }
    bsResetSwipeLayers(state);
  }, SWIPE_SLIDE_MS + 20);
}

/**
 * @param {TouchList} list
 * @param {number} id
 * @returns {Touch | null}
 */
function bsFindTouch(list, id) {
  return Array.from(list).find((touch) => touch.identifier === id) ?? null;
}

/* Touch events, not pointer events, and deliberately so: the move listener has
   to be non-passive. Pointer events here were passive by necessity, which left
   preventDefault() unavailable and the gesture at the mercy of `touch-action`
   alone — iOS Safari claims a horizontal drag anyway and fires pointercancel
   mid-swipe, which read as the image springing back under the finger.

   Scoped to the element rather than the document so the non-passive listener
   costs the rest of the page nothing. Touch events never fire for a mouse, so
   desktop is untouched. */
const pdpMain = /** @type {HTMLElement | null} */ (document.querySelector('.pdp__main'));

if (pdpMain) {
  pdpMain.addEventListener(
    'touchstart',
    (event) => {
      // A second finger means pinch, not swipe — hand the image back and get out
      // of the way (spring back rather than snap, the pinch is still running).
      if (bsSwipe) {
        const running = bsSwipe;
        bsSwipe = null;
        if (running.target) bsSettleSwipe(running, false);
        return;
      }
      if (event.touches.length > 1) return;
      const touch = event.touches[0];
      const gallery = pdpMain.closest('.pdp__gallery');
      const media = /** @type {HTMLElement | null} */ (
        pdpMain.querySelector('.frozen-media:not(.pdp__peek)')
      );
      if (!touch || !gallery || !media) return;
      // Mid-animation: let it land rather than fight it.
      if (pdpMain.dataset.sliding) return;
      // Single-image products (or a variant filter that left one thumb) have
      // nothing to swipe to — don't arm the gesture at all.
      if (bsVisibleThumbs(gallery).length < 2) return;
      bsSwipe = {
        id: touch.identifier,
        x: touch.clientX,
        y: touch.clientY,
        lastX: touch.clientX,
        main: pdpMain,
        gallery,
        media,
        axis: null,
        direction: 0,
        peek: null,
        target: null,
        width: pdpMain.clientWidth || 1,
      };
    },
    { passive: true }
  );

  pdpMain.addEventListener(
    'touchmove',
    (event) => {
      if (!bsSwipe) return;
      const touch = bsFindTouch(event.touches, bsSwipe.id);
      if (!touch) return;
      const deltaX = touch.clientX - bsSwipe.x;
      const deltaY = touch.clientY - bsSwipe.y;

      if (!bsSwipe.axis) {
        if (Math.abs(deltaX) < SWIPE_AXIS_LOCK && Math.abs(deltaY) < SWIPE_AXIS_LOCK) return;
        // Lock the axis on the first decisive movement. Vertical belongs to the
        // page: drop the gesture and let the browser scroll as usual.
        bsSwipe.axis = Math.abs(deltaX) > Math.abs(deltaY) ? 'x' : 'y';
        if (bsSwipe.axis === 'y') {
          bsSwipe = null;
          return;
        }
      }
      if (bsSwipe.axis !== 'x') return;

      // Horizontal is ours from here on. THIS is what keeps the browser from
      // turning the drag into a scroll and cancelling it half-way.
      if (event.cancelable) event.preventDefault();
      bsSwipe.lastX = touch.clientX;
      if (REDUCED_MOTION) return;

      // Direction can flip mid-drag (drag left, change your mind, drag back past
      // the start) — re-aim the peek at the other neighbour when it does.
      const direction = deltaX < 0 ? 1 : -1;
      if (direction !== bsSwipe.direction) bsPreparePeek(bsSwipe, direction);
      if (bsSwipe.target) bsDrawSwipe(bsSwipe, deltaX);
    },
    { passive: false }
  );

  pdpMain.addEventListener(
    'touchend',
    (event) => {
      if (!bsSwipe) return;
      const state = bsSwipe;
      const touch = bsFindTouch(event.changedTouches, state.id);
      if (!touch && event.touches.length) return;
      bsSwipe = null;
      if (state.axis !== 'x') return;

      bsSwipeEndedAt = performance.now();
      const deltaX = (touch ? touch.clientX : state.lastX) - state.x;
      const passed = Math.abs(deltaX) >= SWIPE_MIN_DISTANCE;
      if (REDUCED_MOTION) {
        // No tracking layer to settle: step straight to the neighbour.
        if (passed) bsStepMedia(state.gallery, deltaX < 0 ? 1 : -1);
        return;
      }
      if (!state.target) return;
      bsSettleSwipe(state, passed);
    },
    { passive: true }
  );

  pdpMain.addEventListener(
    'touchcancel',
    () => {
      if (!bsSwipe) return;
      const state = bsSwipe;
      bsSwipe = null;
      if (!state.target) return;
      // A cancel this late is the system interrupting (call, gesture recogniser).
      // The finger had already said where it wanted to go — honour it rather than
      // throwing the drag away.
      const passed = Math.abs(state.lastX - state.x) >= SWIPE_MIN_DISTANCE;
      bsSwipeEndedAt = performance.now();
      bsSettleSwipe(state, passed);
    },
    { passive: true }
  );
}

document.addEventListener(
  'click',
  (event) => {
    if (performance.now() - bsSwipeEndedAt > SWIPE_CLICK_GUARD_MS) return;
    if (!(event.target instanceof Element) || !event.target.closest('.pdp__main')) return;
    // Capture phase, so this lands before any handler on the image itself.
    event.stopPropagation();
    event.preventDefault();
  },
  true
);

/* Show only the selected variant's media (+ media assigned to no variant);
   swap the main image to the variant's featured media. */
function bsFilterGallery(variant) {
  const gallery = document.querySelector('.pdp__gallery');
  if (!gallery || !variant) return;
  // Probe picks show the flakon hero instead of the vial shot (the vial stays
  // reachable as a thumb). The whole strip is visible, since no media is
  // filtered away for a Probe. Resetting to the hero has to be ACTIVE, exactly
  // like the variant path below: just leaving the current media in place would
  // keep whatever the visitor last opened — pick the Zutaten image, then the
  // Probe, and both the hero and its sticky-bar mirror stayed on Zutaten.
  if (/2\s?ml/i.test(variant.title || '')) {
    gallery.querySelectorAll('.pdp__thumb').forEach((t) => {
      t.hidden = false;
    });
    const heroId = gallery.getAttribute('data-hero-media-id');
    const hero = heroId && gallery.querySelector(`.pdp__thumb[data-media-id="${heroId}"]`);
    if (hero) {
      const strip = hero.parentElement;
      if (strip && strip.firstElementChild !== hero) strip.prepend(hero);
      bsSelectMedia(gallery, hero);
    }
    return;
  }
  const curId = String(variant.id);
  gallery.querySelectorAll('.pdp__thumb').forEach((t) => {
    const ids = (t.dataset.variantIds || '').trim();
    t.hidden = ids !== '' && !ids.split(/\s+/).includes(curId);
  });
  const fmId = variant.featured_media && String(variant.featured_media.id);
  let target = fmId && gallery.querySelector(`.pdp__thumb[data-media-id="${fmId}"]`);
  if (!target) target = gallery.querySelector('.pdp__thumb:not([hidden])');
  // Move the variant's own media to the front, mirroring the Liquid ordering on
  // first paint. Without this the thumb keeps its initial DOM slot, so picking
  // the Probe leaves the variant-less vibe shot sitting in position 1.
  const strip = target && target.parentElement;
  if (strip && strip.firstElementChild !== target) strip.prepend(target);
  if (target) bsSelectMedia(gallery, target);
}

/* --- Sticky ATC: reveal when the buy box leaves the viewport --- */
const buyBox = document.querySelector('.pdp__buy');
const stickyBar = document.querySelector('.pdp-sticky');
if (buyBox && stickyBar) {
  const update = () => {
    const rect = buyBox.getBoundingClientRect();
    stickyBar.classList.toggle('show', rect.bottom < 80);
  };
  window.addEventListener('scroll', update, { passive: true });
  update();
  // Sticky ATC submits the main product form
  stickyBar.querySelector('[data-bs-sticky-atc]')?.addEventListener('click', (event) => {
    event.preventDefault();
    if (event.currentTarget.disabled) return;
    const form = document.querySelector('.pdp__buybox product-form-component form');
    if (form) form.requestSubmit();
  });
}

/* --- PDP performance bars: values come from the product's custom.rating_*
       metafields, rendered into data-value by main-product.liquid. Ratings are
       on a 0-10 scale; the bars are a percentage of 10. --- */
const ratings = document.querySelector('[data-bs-ratings]');
if (ratings) {
  const fmt = (v) => v.toFixed(1).replace('.', ',');
  /** @type {Array<[HTMLElement, number]>} */
  const bars = [];

  for (const metric of ratings.querySelectorAll('[data-metric]')) {
    const value = parseFloat(metric.dataset.value ?? '');
    if (Number.isNaN(value)) {
      metric.remove();
      continue;
    }
    const bar = /** @type {HTMLElement|null} */ (metric.querySelector('[data-bar-fill]'));
    const display = metric.querySelector('[data-value-display]');
    if (display) display.textContent = fmt(value);
    if (bar) {
      if (!REDUCED_MOTION) bar.style.transition = 'width 0.6s cubic-bezier(0.4, 0, 0.2, 1)';
      bars.push([bar, value]);
    }
  }

  if (!bars.length) {
    ratings.remove();
  } else {
    requestAnimationFrame(() => {
      ratings.hidden = false;
      requestAnimationFrame(() => {
        for (const [bar, value] of bars) bar.style.width = `${(value / 10) * 100}%`;
      });
    });
  }
}

/* --- Variant change: filter the gallery + mirror the live price into the sticky bar --- */
document.addEventListener(ThemeEvents.variantUpdate, (event) => {
  const variant = event.detail?.resource;
  bsFilterGallery(variant);
  // Mirror availability into the sticky ATC. The inline buy button is handled
  // server-side + by product-form.js, but the floating sticky mirror has no
  // such wiring — without this it stays clickable on a sold-out variant.
  const stickyAtc = document.querySelector('.pdp-sticky [data-bs-sticky-atc]');
  if (stickyAtc && variant) {
    const available = variant.available !== false;
    stickyAtc.disabled = !available;
    stickyAtc.classList.toggle('is-disabled', !available);
    stickyAtc.textContent = available ? 'In den Warenkorb' : 'Ausverkauft';
  }
  if (variant) {
    const stockLine = document.querySelector('[data-bs-stock]');
    const stockText = stockLine?.querySelector('[data-bs-stock-text]');
    if (stockLine && stockText) {
      const available = variant.available !== false;
      stockLine.classList.toggle('is-out', !available);
      stockText.textContent = available ? 'Auf Lager - in 1-3 Werktagen bei dir!' : 'Nicht vorrätig';
    }
    // The size option's position is baked in by Liquid — it is not always option1.
    const stickyVariant = document.querySelector('[data-bs-sticky-variant]');
    if (stickyVariant) {
      const position = stickyVariant.dataset.bsSizePosition || '1';
      stickyVariant.textContent = variant[`option${position}`] || '';
    }
  }
  // product-price replaces the main price container after this event; copy on next tick.
  setTimeout(() => {
    const main = document.querySelector('.pdp__price');
    const sticky = document.querySelector('.pdp-sticky__price');
    if (main && sticky) sticky.textContent = main.textContent.trim();
  }, 0);
});

/* --- Reduced-motion: disable max-height animation --- */
if (REDUCED_MOTION) {
  document.querySelectorAll('.acc__body').forEach((body) => {
    body.style.transition = 'none';
  });
}

/* --- Announcement bar: rotate messages every 5s + working prev/next arrows
       (replaces the old theme's multi-message bar; texts are section settings). --- */
document.querySelectorAll('[data-announce-rotator]').forEach((rotator) => {
  const msgs = [...rotator.querySelectorAll('.hdrB__announce-msg')];
  if (msgs.length < 2) return;
  let idx = msgs.findIndex((m) => m.classList.contains('is-active'));
  if (idx < 0) idx = 0;
  let timer;
  const show = (n) => {
    msgs[idx].classList.remove('is-active');
    idx = (n + msgs.length) % msgs.length;
    msgs[idx].classList.add('is-active');
  };
  const start = () => {
    timer = setInterval(() => show(idx + 1), 5000);
  };
  const reset = () => {
    clearInterval(timer);
    start();
  };
  rotator.querySelector('[data-announce-prev]')?.addEventListener('click', () => {
    show(idx - 1);
    reset();
  });
  rotator.querySelector('[data-announce-next]')?.addEventListener('click', () => {
    show(idx + 1);
    reset();
  });
  start();
});
